/**
 * Reusable "Coming Soon" template for features that are planned but not yet released.
 *
 * Usage:
 *   <Route path="reports/forecast" element={<ComingSoonPage />} />
 *   <ComingSoonPage title="Forecast" description="Predictive sales view." eta="Q3 2026" />
 *
 * Falls back to deriving the title from the current navigation breadcrumb when no
 * `title` prop is provided, so it can be dropped into the router with zero config.
 */
import { Box, Typography, Paper, Chip, Stack, Button } from '@mui/material'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import HomeIcon from '@mui/icons-material/Home'
import { useLocation, useNavigate } from 'react-router-dom'
import { navigation } from '../config/navigation'
import type { NavItem } from '../config/navigation'
import type { UnbuildStatus } from '../config/pageBuildStatus'

export interface ComingSoonPageProps {
  /** Feature title. If omitted, derived from the active nav breadcrumb. */
  title?: string
  /** Short description of what the feature will do. */
  description?: string
  /** Expected release window, e.g. "Q3 2026" or "August 2026". */
  eta?: string
  /** Hide the back / home action row. */
  hideActions?: boolean
  /** When embedded inside another page's chrome, suppress the outer breadcrumb + h5. */
  embedded?: boolean
  /** Lifecycle flag from pageBuildStatus — drives the badge label/colour. */
  status?: UnbuildStatus
}

const STATUS_META: Record<UnbuildStatus, { label: string; color: 'warning' | 'info' }> = {
  'to-build': { label: 'In development', color: 'warning' },
  'to-test': { label: 'In testing — pending QA', color: 'info' },
}

function findCrumbs(items: NavItem[], pathname: string, search: string): string[] {
  for (const item of items) {
    const fullPath = item.path
    if (fullPath.includes('?')) {
      const [base, qs] = fullPath.split('?')
      if (pathname === base && search.includes(qs)) return [item.label]
    } else if (pathname === fullPath && !item.children) {
      return [item.label]
    }
    if (item.children) {
      const sub = findCrumbs(item.children, pathname, search)
      if (sub.length > 0) return [item.label, ...sub]
    }
  }
  return []
}

export default function ComingSoonPage({
  title,
  description,
  eta,
  hideActions,
  embedded,
  status,
}: ComingSoonPageProps) {
  const statusMeta = status ? STATUS_META[status] : null
  const location = useLocation()
  const navigate = useNavigate()
  const crumbs = findCrumbs(navigation, location.pathname, location.search)
  const resolvedTitle = title ?? (crumbs.length > 0 ? crumbs[crumbs.length - 1] : 'Feature')
  const parentCrumbs = title ? [] : crumbs.slice(0, -1)

  return (
    <Box
      sx={
        embedded
          ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
          : undefined
      }
    >
      {!embedded && parentCrumbs.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {parentCrumbs.join(' / ')}
        </Typography>
      )}
      {!embedded && (
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {resolvedTitle}
        </Typography>
      )}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 4, sm: 6 },
          mt: embedded ? 0 : 2,
          flex: embedded ? 1 : undefined,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          textAlign: 'center',
          borderStyle: 'dashed',
          bgcolor: 'background.default',
        }}
      >
        <Box
          sx={{
            width: 88,
            height: 88,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.50',
            color: 'primary.main',
          }}
        >
          <RocketLaunchIcon sx={{ fontSize: 44 }} />
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip label="Coming soon" color="warning" size="small" />
          {statusMeta && (
            <Chip label={statusMeta.label} color={statusMeta.color} size="small" variant="outlined" />
          )}
        </Stack>
        <Typography variant="h6" fontWeight={600}>
          {resolvedTitle} is on the way
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 480 }}
        >
          {description ??
            'This feature is being built and will be available in a future release. Thanks for your patience.'}
        </Typography>
        {eta && (
          <Typography variant="caption" color="text.secondary">
            Expected release: <strong>{eta}</strong>
          </Typography>
        )}
        {!hideActions && (
          <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              Go back
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/')}
            >
              Home
            </Button>
          </Stack>
        )}
      </Paper>
    </Box>
  )
}
